import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import posthog from '../../posthog';
import userflow from '../../userflow';
import { getSignupAttribution, getUserflowIdentity } from '../auth.api';
import { clearPendingSchedulerEvents, flushPendingSchedulerEvents } from './capture-scheduler-event';

/**
 * Push identity and context into the third-party analytics SDKs.
 *
 * Extracted from user-state-context so that file holds auth, permissions and
 * company switching without three effects of write-only vendor plumbing
 * alongside them. This hook consumes state and pushes outward — nothing in the
 * app reads from it, which is why it is a hook rather than a context.
 *
 * Deliberately three separate effects: Pendo and PostHog have different
 * lifecycle branches (PostHog used to live inside Pendo's `else if`, so it was
 * skipped entirely once Pendo was already initialized), and Sentry depends on
 * `token`, which the other two do not.
 */
export function useAnalyticsIdentity({
  user,
  asCompany,
  userRole,
  token
}) {
  const aliasedUserIdRef = useRef(null);
  const attributionUserIdRef = useRef(null);
  const userflowIdentityRef = useRef(null);
  const mergedSignupRef = useRef(null);

  /**
   * Keep Pendo in sync with current state.
   *
   * @listens user
   * @listens asCompany
   */
  useEffect(() => {
    const isPlg = asCompany?.account_type === 'PLG';
    if (user && asCompany && window.pendo.host && window.pendo?.isReady()) {
      //pendo is already initalized, it just needs an update
      window.pendo.updateOptions({
        visitor: {
          company: asCompany.id,
          companyName: asCompany.name,
          permissions: user.permissions,
          role: user?.is_superuser ? 'superuser' : userRole ? userRole?.role_name : 'None',
          is_plg: isPlg
        },
        account: {
          id: asCompany.id,
          name: asCompany.name,
          account_type: asCompany.account_type || null,
          is_plg: isPlg
        }
      });
    } else if (user && asCompany) {
      window.pendo.initialize({
        visitor: {
          id: user.id,
          // Required if user is logged in, default creates anonymous ID
          email: user.email,
          // Recommended if using Pendo Feedback, or NPS Email
          full_name: user.first_name + ' ' + user.last_name,
          // Recommended if using Pendo Feedback
          company: asCompany.id,
          companyName: asCompany.name,
          permissions: user.permissions,
          role: user?.is_superuser ? 'superuser' : userRole ? userRole?.role_name : 'None',
          industry: asCompany.naics_title,
          is_plg: isPlg
        },
        account: {
          id: asCompany.id,
          // Required if using Pendo Feedback, default uses the value 'ACCOUNT-UNIQUE-ID'
          name: asCompany.name,
          account_type: asCompany.account_type || null,
          is_plg: isPlg
        }
      });
    }
  }, [user, asCompany]);

  /**
   * Keep PostHog in sync with current state.
   *
   * Separate from the Pendo effect above: this used to live inside its
   * `else if` branch, so once Pendo was already initialized every
   * identify, alias, group and super-property write was skipped.
   *
   * @listens user
   * @listens asCompany
   */
  useEffect(() => {
    // Logged out: Scheduler events still waiting on a company record are dropped, not sent.
    if (!asCompany) clearPendingSchedulerEvents();
    if (!user || !asCompany) return;
    const isPlg = asCompany?.account_type === 'PLG';
    const identityId = String(user.id);

    // Employees are still identified — filter them out in PostHog dashboards
    // via `is_internal_user`. Opting out (previous behavior) stranded each
    // employee's pre-opt-out anonymous session as a permanent ghost person.
    // Reads the `cinch_employee` role from `internal_roles` so the
    // definition lives in exactly one place (INTERNAL_ROLES on the backend).
    const isInternalUser = !!user.internal_roles?.includes('cinch_employee');
    if (posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing();
    }
    if (user.email && aliasedUserIdRef.current !== identityId) {
      aliasedUserIdRef.current = identityId;
      posthog.alias(user.email.toLowerCase(), identityId);
    }
    posthog.identify(identityId, {
      email: user.email,
      name: user.first_name + ' ' + user.last_name,
      company_id: asCompany.id,
      company_name: asCompany.name,
      role: user?.is_superuser ? 'superuser' : userRole ? userRole?.role_name : 'None',
      is_internal_user: isInternalUser
    });

    // PLG sign-ups are identified by email on public-tools before they ever log
    // in, so PostHog refuses the alias above (two identified persons) and the
    // sign-up events stay on a second person. $merge_dangerously folds that
    // person into this one. Capture gives no delivery signal, so this is sent
    // once per page load rather than remembered: a repeat on an already-merged
    // pair is a server-side no-op.
    if (isPlg && user.email && mergedSignupRef.current !== identityId) {
      mergedSignupRef.current = identityId;
      posthog.capture('$merge_dangerously', {
        alias: user.email.toLowerCase()
      });
    }
    posthog.group('company', String(asCompany.id), {
      name: asCompany.name,
      account_type: asCompany.account_type || null,
      is_plg: isPlg
    });
    if (asCompany.account) {
      posthog.group('account', String(asCompany.account), {
        name: asCompany.account_name || null,
        account_type: asCompany.account_type || null,
        is_plg: isPlg
      });
    }
    posthog.register({
      is_internal_user: isInternalUser,
      company_type: asCompany.account_type || null
    });

    // After group/register so events held for the cookie stub carry the company group.
    flushPendingSchedulerEvents(asCompany);
    if (isPlg && attributionUserIdRef.current !== identityId) {
      attributionUserIdRef.current = identityId;
      getSignupAttribution().then(({
        data
      }) => {
        // The identity can change while this is in flight (company switch,
        // re-login). Writing a previous user's UTMs onto the current person
        // would silently mis-attribute them, so drop a stale response.
        if (attributionUserIdRef.current !== identityId) return;
        if (data && Object.keys(data).length) {
          posthog.register(data);
          posthog.setPersonProperties(data);
        }
      }).catch(e => {
        console.warn('Failed to load signup attribution for PostHog:', e);
        if (attributionUserIdRef.current === identityId) {
          attributionUserIdRef.current = null;
        }
      });
    }
  }, [user, asCompany]);

  /**
   * Keep Userflow in sync with current state (CIN-7217).
   *
   * Its own effect for the same reason PostHog has one, plus a difference none
   * of the others have: Userflow will not accept an identity without a
   * server-issued signature, so this branch is async and can decline to
   * identify at all.
   *
   * No `reset()` on a company switch — that retires the Userflow user and makes
   * a second one for the same person. Re-running identify and group updates the
   * attributes in place. `reset()` belongs to sign-out, and lives there.
   *
   * @listens user
   * @listens asCompany
   */
  useEffect(() => {
    // `asCompany` is set first as little more than an id and replaced once
    // /companies/<id> lands. Waiting for `name` keeps a half-loaded company out
    // of Userflow entirely — identifying early would write `is_plg: false` for a
    // PLG account and mis-target every flow aimed at that audience until the
    // second write corrected it.
    if (!user || !asCompany?.name || !userflow.isEnabled()) return;
    const identityId = String(user.id);
    const companyId = String(asCompany.id);
    const identityKey = `${identityId}:${companyId}`;

    // `asCompany` arrives in two stages: an early object carrying little more
    // than the id, then the full record once /companies/<id> lands. Only the
    // signature request is cached per identity — the attributes are pushed on
    // every run, or Userflow keeps the first, emptier version forever and a PLG
    // company reads as `is_plg: false`.
    const push = signatures => {
      // Unsigned calls are rejected by Userflow, so stay unidentified rather
      // than firing one it will refuse.
      if (!signatures?.user_signature) return;
      const isPlg = asCompany?.account_type === 'PLG';
      userflow.identify(identityId, {
        name: `${user.first_name} ${user.last_name}`.trim(),
        email: user.email,
        role: user?.is_superuser ? 'superuser' : userRole ? userRole?.role_name : 'None',
        is_internal_user: !!user.internal_roles?.includes('cinch_employee'),
        company_id: companyId,
        company_name: asCompany.name || null,
        account_type: asCompany.account_type || null,
        is_plg: isPlg
      }, {
        signature: signatures.user_signature
      });
      if (signatures.group_signature) {
        userflow.group(companyId, {
          name: asCompany.name || null,
          account_type: asCompany.account_type || null,
          is_plg: isPlg,
          industry: asCompany.naics_title || null
        }, {
          signature: signatures.group_signature
        });
      }
    };

    // Signatures cover ids only, so they survive an attribute refresh and this
    // costs one request per identity rather than one per render.
    if (userflowIdentityRef.current?.key === identityKey) {
      push(userflowIdentityRef.current.signatures);
      return undefined;
    }
    let superseded = false;
    getUserflowIdentity(companyId).then(({
      data
    }) => {
      // The identity can change while this is in flight (company switch,
      // re-login). Writing the previous one would mis-attribute the session.
      if (superseded) return;
      userflowIdentityRef.current = {
        key: identityKey,
        signatures: data
      };
      push(data);
    }).catch(e => {
      console.warn('Failed to load Userflow identity signature:', e);
    });
    return () => {
      superseded = true;
    };
  }, [user, asCompany]);

  /**
   * Keep Sentry context in sync with current state.
   *
   * Its own effect because it reads `token`, which the PostHog effect above
   * does not — folding it in there would either leave `token` out of the
   * dependencies (stale context) or re-fire identify/group/register on every
   * token change.
   *
   * @listens asCompany
   * @listens token
   */
  useEffect(() => {
    if (!asCompany) return;
    Sentry.setContext('working_as', {
      id: asCompany.id,
      name: asCompany.name
    });
    Sentry.setContext('hasToken', {
      hasToken: !!token
    });
  }, [asCompany, token]);
}